import { useState, useImperativeHandle, forwardRef } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import PropTypes from 'prop-types';

import Snackbar from '@zenbusiness/zui/Snackbar';

/**
 * A Toast component that displays temporary messages using zui Snackbar
 * @param {object} props - Component props
 * @param {string} [props.message=''] - The message text to display in the toast
 * @param {number} [props.autoHideDuration=3000] - Time in milliseconds before auto-hiding (0 to disable)
 * @param {string} [props.anchorOrigin={ vertical: 'bottom', horizontal: 'right' }] - Position of the toast
 * @param {Function} [props.onClose] - Callback fired when the toast is closed
 * @param {boolean} [props.open=false] - Whether the toast is open by default
 * @param {import('react').ReactNode} [props.action] - Action to display in the toast
 */
const Toast = forwardRef(
  (
    {
      message: messageProp = '',
      autoHideDuration = 3000,
      anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
      onClose,
      open: openProp = false
    },
    ref
  ) => {
    const [open, setOpen] = useState(openProp);
    const [message, setMessage] = useState(messageProp);

    // Handle close event
    const handleClose = (event, reason) => {
      if (reason === 'clickaway') {
        return;
      }
      setOpen(false);
      if (onClose) {
        onClose(event, reason);
      }
    };

    // Expose methods to parent components via ref
    useImperativeHandle(ref, () => ({
      show: (newMessage) => {
        if (newMessage) {
          setMessage(newMessage);
        }
        setOpen(true);
      },
      hide: () => {
        setOpen(false);
      }
    }));

    return (
      <Snackbar
        anchorOrigin={anchorOrigin}
        autoHideDuration={autoHideDuration}
        dismissable
        open={open}
        slots={{
          startIcon: <CheckIcon />
        }}
        title={message}
        variant="promo"
        onClose={handleClose}
      />
    );
  }
);

Toast.displayName = 'Toast';

Toast.propTypes = {
  message: PropTypes.string,
  autoHideDuration: PropTypes.number,
  anchorOrigin: PropTypes.shape({
    vertical: PropTypes.oneOf(['top', 'bottom']),
    horizontal: PropTypes.oneOf(['left', 'center', 'right'])
  }),
  onClose: PropTypes.func,
  open: PropTypes.bool
};

export default Toast;
